import { useEffect, useRef, useState } from "react"
import Api from "../../api/api"
import { userLabel, UserSearchResult } from "../../api/models"
import Avatar from "./Avatar"
import Spinner from "./Spinner"

const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MIN_CHARS = 2

// Debounced Cognito-backed typeahead: picking a match narrows the caller's
// listing to that user's exact id. Collapses into a dismissible chip while a
// user is selected. disabledIds greys out non-selectable results (users
// already on the caller's list), with disabledHint explaining why.
export default function UserFilter({ selected, onSelect, accessToken, placeholder = "Search user by email or name…", disabledIds, disabledHint }: {
  selected: UserSearchResult | null
  onSelect: (user: UserSearchResult | null) => void
  accessToken: string
  placeholder?: string
  disabledIds?: string[]
  disabledHint?: string
}) {
  const [text, setText] = useState("")
  const [results, setResults] = useState<UserSearchResult[] | undefined>(undefined)
  const [searching, setSearching] = useState(false)
  const requestSeq = useRef(0)

  useEffect(() => {
    const query = text.trim()
    if (query.length < SEARCH_MIN_CHARS) {
      requestSeq.current++
      setResults(undefined)
      setSearching(false)
      return
    }
    const seq = ++requestSeq.current
    setSearching(true)
    const timer = setTimeout(async () => {
      try {
        const page = await Api.searchUsers({ search: query, accessToken })
        if (seq !== requestSeq.current) return
        setResults(page.users)
      } catch {
        if (seq === requestSeq.current) setResults([])
      } finally {
        if (seq === requestSeq.current) setSearching(false)
      }
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [text, accessToken])

  if (selected) {
    return (
      <button
        className="skus-filter-chip skus-filter-chip-active icon-chip"
        onClick={() => onSelect(null)}
        title={selected.email}
      >
        <Avatar imageUrl={selected.imageUrl} size={18} />
        {userLabel(selected.id, selected)} ✕
      </button>
    )
  }

  return (
    <div className="typeahead">
      <input
        className="filter-input"
        type="text"
        // the placeholder mentions "email", which makes browsers offer their
        // saved-address autofill over our own typeahead dropdown; Chrome
        // ignores autocomplete="off" for address-ish fields, but an
        // unrecognized token reliably disables autofill
        autoComplete="user-search"
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {text.trim().length >= SEARCH_MIN_CHARS && (
        <div className="typeahead-results">
          {results === undefined || (searching && results.length === 0) ? (
            <div className="typeahead-empty"><Spinner size={12} /> Searching…</div>
          ) : results.length === 0 ? (
            <div className="typeahead-empty">No matching users</div>
          ) : (
            results.map((user) => {
              const alreadyAdded = disabledIds?.includes(user.id) ?? false
              return (
                // onMouseDown so selection wins over the input losing focus
                <button
                  key={user.id}
                  className="typeahead-result"
                  title={user.email}
                  disabled={alreadyAdded}
                  style={alreadyAdded ? { opacity: 0.45, cursor: "default" } : undefined}
                  onMouseDown={alreadyAdded ? undefined : () => {
                    onSelect(user)
                    setText("")
                    setResults(undefined)
                  }}
                >
                  <Avatar imageUrl={user.imageUrl} size={24} />
                  <span className="typeahead-result-text">
                    {userLabel(user.id, user)}
                    {alreadyAdded && disabledHint && (
                      <span style={{ color: "var(--nfa-light-grey)", fontStyle: "italic" }}> — {disabledHint}</span>
                    )}
                  </span>
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
